'use client'

import { useEffect, useState, useCallback } from 'react'
import { X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import useFlightBookingStore from '@/lib/useFlightBookingStore';
import { useSearchParams } from "next/navigation";
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import api from '@/app/api'
import { toast } from 'sonner';
import { AxiosError } from 'axios'

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EmailFlightInfoBar() {
  // State management
  const [emails, setEmails] = useState<string[]>([])
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState('')
  const [showDialog, setShowDialog] = useState(false)
  const [showAlert, setShowAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // External data
  const { viewData, setshareMails, shareMails: shareMailsData } = useFlightBookingStore();
  const searchParams = useSearchParams();

  // Hide alert when flight data is available
  useEffect(() => {
    if (viewData.length > 0) {
      setShowAlert(false);
    }
  }, [viewData])

  // Memoized handlers
  const validateEmail = useCallback((email: string) => {
    return EMAIL_REGEX.test(email);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      const newEmail = inputValue.trim()
      
      // Check if email already exists
      if (emails.includes(newEmail)) {
        setError('This email is already added')
        return
      }

      // Validate email
      if (validateEmail(newEmail)) {
        setEmails(prevEmails => [...prevEmails, newEmail])
        setInputValue('')
        setError('')
      } else {
        setError('Please enter a valid email')
      }
    }
  }, [inputValue, emails, validateEmail]);

  const removeEmail = useCallback((emailToRemove: string) => {
    setEmails(emails => emails.filter(email => email !== emailToRemove))
  }, []);

  const handleOpenClick = () => {
    if (viewData.length === 0) {
      setShowDialog(false)
      setShowAlert(true);
    } else {
      setShowDialog(!showDialog)
    }
  };

  const handleShareMails = useCallback(async(isWithOutPrices: boolean) => {
    // Parse search params once and reuse
    const queryData = JSON.parse(searchParams?.get("data") || "{}")
    const queryMetaData = JSON.parse(searchParams?.get("metadata") || "{}")
    const queryTripType = searchParams?.get("tripType") || ""

    const mails = {
      isWithOutPrice: isWithOutPrices,
      mode: "Email",
      data: viewData,
      searchQuery: {
        queryData,
        queryMetaData,
        queryTripType
      },
      toEmail: emails
    }

    try {
      setIsLoading(true); // Show loading state if you have one
      
      const response = await api.post<{message:string}>('/flight/email', mails);
      
      const result = response.data;
      
        toast.success(result.message);
        // Close your alert/modal here
        setShowDialog(false);
    } catch (error) {
        if(error instanceof AxiosError){
            toast.error(error.response?.data.message);
        }else{
            toast.error('Failed to send flight details. Please try again.');
        }
    } finally {
      setIsLoading(false); // Hide loading state
    }

  }, [searchParams, viewData, emails, setshareMails]);


  
  return (
    <>
    {showAlert && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
          <Alert className="bg-white shadow-lg">
            <AlertTitle className="flex items-center justify-between">
              No Flights Available
              <button onClick={() => setShowAlert(false)}>
                <X />
              </button>
            </AlertTitle>
            <AlertDescription>
              There are no flight details available for your query.
            </AlertDescription>
          </Alert>
        </div>
      )}
      <Dialog open={showDialog} onOpenChange={handleOpenClick}>
      <DialogTrigger>
        <button onClick={handleOpenClick} className="mr-3">
          Send
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md p-0 rounded-md overflow-hidden">
        <div className="p-6">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-2xl font-medium text-gray-800">
              Flights Quotation
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Email 
              </label>
              <div className="relative">
                <div className={`min-h-[40px] w-full border rounded-md p-2 flex flex-wrap gap-2 ${error ? 'border-red-500' : ''}`}>
                  {emails.map((email, index) => (
                    <div
                      key={index}
                      className="bg-gray-100 px-2 py-1 rounded-md flex items-center gap-1"
                    >
                      <span className="text-sm">{email}</span>
                      <button
                        onClick={() => removeEmail(email)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value)
                      setError('') // Clear error when typing
                    }}
                    onKeyDown={handleKeyDown}
                    className="flex-1 min-w-[120px] outline-none bg-transparent"
                    placeholder={emails.length === 0 ? "Enter Email Address And Press Enter" : ""}
                  />
                </div>
                {error && (
                  <span className="text-red-500 text-xs mt-1 absolute -bottom-5 left-0">
                    {error}
                  </span>
                )}
              </div>
              <div className="mt-4 h-0.5 bg-primary"></div>
            </div>
            
            <div className="pt-4 flex justify-center space-x-4">
              <Button 
                disabled={emails.length === 0}
                onClick={() => handleShareMails(false)}
              >
                With Price
              </Button>
              <Button 
                disabled={emails.length === 0}
                onClick={() => handleShareMails(true)}
              >
                Without Price
              </Button>
            </div>
          </div>
        </div>
        
        <button 
          onClick={() => setShowDialog(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      </DialogContent>
    </Dialog>
    </>
  )
}